import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { AlertTriangle, Loader2, Trash2, Package, RotateCcw, ClipboardList, Truck } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Modale bloquante — purge l'historique matériel/équipement d'un collaborateur
 * (affectations + restitutions + demandes). Réservée aux administrateurs.
 *
 * Props:
 *  - open, onOpenChange
 *  - collaborateur : { id, name, email }
 *  - onDone(result) : callback après succès
 */
export default function PurgeCollabEquipmentDialog({ open, onOpenChange, collaborateur, onDone }) {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState({ loading: false, counts: null });

  const cid = collaborateur?.id;

  const loadPreview = useCallback(async () => {
    if (!cid) return;
    setPreview((p) => ({ ...p, loading: true }));
    try {
      const { data } = await api.get(`/collaborateurs/${cid}/equipment-history/purge-preview`);
      setPreview({ loading: false, counts: data.counts });
    } catch {
      setPreview({ loading: false, counts: null });
    }
  }, [cid]);

  useEffect(() => {
    if (open) {
      setConfirmText("");
      setBusy(false);
      loadPreview();
    }
  }, [open, loadPreview]);

  const isConfirmed = confirmText.trim().toUpperCase() === "PURGE";
  const total = preview.counts?.total ?? 0;
  const nothingToPurge = !preview.loading && preview.counts && total === 0;

  const submit = async () => {
    if (!isConfirmed) {
      toast.error("Tapez PURGE pour confirmer.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/collaborateurs/${cid}/equipment-history/purge`,
        null,
        { params: { confirm: "PURGE" } },
      );
      toast.success(`Purge effectuée : ${data.total} entrée(s) supprimée(s)`);
      onDone?.(data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la purge");
    } finally {
      setBusy(false);
    }
  };

  const rows = [
    { key: "assignments", label: "Affectations matériel", icon: <Package size={13} className="text-zinc-500" /> },
    { key: "returns", label: "Restitutions", icon: <RotateCcw size={13} className="text-zinc-500" /> },
    { key: "requests", label: "Demandes", icon: <ClipboardList size={13} className="text-zinc-500" /> },
    { key: "site_deliveries", label: "Livraisons de site", icon: <Truck size={13} className="text-zinc-500" /> },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="purge-collab-equipment-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 text-red-700">
            <Trash2 size={18} className="text-red-600" />
            Purger l&apos;historique matériel
          </DialogTitle>
          <DialogDescription>
            Supprime définitivement <span className="font-medium">toutes</span> les
            affectations, restitutions, demandes et livraisons de site de matériel
            pour <span className="font-medium">{collaborateur?.name || "—"}</span>.
            Ce nettoyage ne modifie pas le collaborateur lui-même ni les stocks
            d&apos;équipements.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className="border border-zinc-200 rounded-sm p-3 bg-zinc-50 space-y-2"
            data-testid="purge-collab-equipment-preview"
          >
            <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium">
              Contenu à supprimer
            </p>
            {preview.loading ? (
              <p className="text-xs text-zinc-500 font-mono inline-flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin" /> Comptage en cours…
              </p>
            ) : !preview.counts ? (
              <p className="text-xs text-zinc-500 font-mono">—</p>
            ) : (
              <div className="space-y-1">
                {rows.map((r) => (
                  <div
                    key={r.key}
                    className="flex items-center justify-between text-xs font-mono"
                    data-testid={`purge-collab-equipment-row-${r.key}`}
                  >
                    <span className="flex items-center gap-1.5 text-zinc-700">
                      {r.icon} {r.label}
                    </span>
                    <span
                      className={
                        preview.counts[r.key] > 0
                          ? "text-red-700 font-semibold"
                          : "text-zinc-400"
                      }
                    >
                      {preview.counts[r.key]}
                    </span>
                  </div>
                ))}
                <div className="border-t border-zinc-200 pt-1.5 flex items-center justify-between text-xs font-mono">
                  <span className="uppercase text-[10px] tracking-wider text-zinc-600">
                    Total
                  </span>
                  <span
                    className={
                      total > 0
                        ? "text-red-700 font-bold text-sm"
                        : "text-emerald-700 font-semibold"
                    }
                    data-testid="purge-collab-equipment-total"
                  >
                    {total}
                    {nothingToPurge && " — rien à supprimer"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <Alert className="border-amber-300 bg-amber-50 text-amber-900">
            <AlertTriangle size={16} className="text-amber-700" />
            <AlertTitle className="text-amber-900">Action irréversible</AlertTitle>
            <AlertDescription className="text-amber-800 text-xs">
              Une trace de la purge sera automatiquement enregistrée dans
              l&apos;historique de sécurité (event
              <span className="font-mono mx-1">collab_equipment_purge</span>) avec
              le détail des entrées supprimées.
            </AlertDescription>
          </Alert>

          <div className="space-y-1">
            <Label htmlFor="purge-collab-equipment-confirm" className="text-xs">
              Tapez <span className="font-mono font-bold text-red-700">PURGE</span> pour confirmer
            </Label>
            <Input
              id="purge-collab-equipment-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="PURGE"
              autoComplete="off"
              data-testid="purge-collab-equipment-confirm-input"
              disabled={nothingToPurge}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            data-testid="purge-collab-equipment-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !isConfirmed || nothingToPurge}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="purge-collab-equipment-submit-btn"
          >
            {busy ? (
              <>
                <Loader2 size={14} className="mr-1.5 animate-spin" /> Purge en cours…
              </>
            ) : (
              "Purger maintenant"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
